// Path: sec-parser/client/src/pages/ReviewQueuePage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, Table, Tag, Button, Typography, Space, Badge,
  Statistic, Row, Col, Empty, Spin,
} from "antd";
import {
  SafetyCertificateOutlined, ClockCircleOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { getReviewQueue } from "../services/api";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const STATUS_CONFIG = {
  PENDING:   { color: "orange", icon: <ClockCircleOutlined />,        label: "Pending" },
  IN_REVIEW: { color: "blue",   icon: <ExclamationCircleOutlined />,  label: "In Review" },
  COMPLETE:  { color: "green",  icon: <CheckCircleOutlined />,        label: "Complete" },
};

export default function ReviewQueuePage() {
  const navigate = useNavigate();
  const [queue, setQueue]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getReviewQueue();
      setQueue(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const pending   = queue.filter((r) => r.status === "PENDING").length;
  const inReview  = queue.filter((r) => r.status === "IN_REVIEW").length;
  const complete  = queue.filter((r) => r.status === "COMPLETE").length;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <div>
          <Title level={3} style={{ color: "#1A3C5E", margin: 0 }}>
            <SafetyCertificateOutlined style={{ marginRight: 8 }} />
            Human Review Queue
          </Title>
          <Text type="secondary">Layer 4 — Analyst sign-off for flagged AI outputs</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </Space>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: "Pending Review",  value: pending,  color: "#faad14" },
          { label: "In Progress",     value: inReview, color: "#1677ff" },
          { label: "Completed",       value: complete, color: "#52c41a" },
          { label: "Total Tasks",     value: queue.length, color: "#1A3C5E" },
        ].map(({ label, value, color }) => (
          <Col span={6} key={label}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Statistic title={label} value={value} valueStyle={{ color, fontSize: 28 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Table
          dataSource={queue}
          rowKey="_id"
          loading={loading}
          locale={{ emptyText: <Empty description="No review tasks yet. Run a benchmark to generate them." /> }}
          columns={[
            {
              title: "Filing",
              dataIndex: ["filingId", "originalName"],
              render: (v, r) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{r.filingId?.companyName || "Unknown Company"}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text>
                </Space>
              ),
            },
            {
              title: "Model",
              dataIndex: "modelId",
              render: (v) => <Tag color="blue">{v?.toUpperCase()}</Tag>,
            },
            {
              title: "Status",
              dataIndex: "status",
              render: (v) => {
                const cfg = STATUS_CONFIG[v] || STATUS_CONFIG.PENDING;
                return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
              },
            },
            {
              title: "Priority Fields",
              dataIndex: "priorityFields",
              render: (v) => (
                <Badge
                  count={v?.length || 0}
                  style={{ background: v?.length > 0 ? "#ff4d4f" : "#52c41a" }}
                  showZero
                />
              ),
            },
            {
              title: "Pending",
              dataIndex: "pendingCount",
              render: (v) => <Text type={v > 0 ? "danger" : "secondary"}>{v ?? "—"}</Text>,
            },
            {
              title: "Validated Score",
              dataIndex: ["parsedResultId", "accuracyScore"],
              render: (v, r) => r.humanValidatedScore != null
                ? <Text strong style={{ color: "#1A3C5E" }}>{r.humanValidatedScore}%</Text>
                : <Text type="secondary">—</Text>,
            },
            {
              title: "Created",
              dataIndex: "createdAt",
              render: (v) => dayjs(v).format("MMM D, h:mm A"),
            },
            {
              title: "Action",
              key: "action",
              render: (_, r) => (
                <Button
                  type="primary"
                  size="small"
                  disabled={r.status === "COMPLETE"}
                  onClick={() => navigate(`/review/${r.parsedResultId?._id || r.parsedResultId}?reviewer=Analyst`)}
                  style={{ background: r.status !== "COMPLETE" ? "#1A3C5E" : undefined }}
                >
                  {r.status === "COMPLETE" ? "Done ✓" : r.status === "IN_REVIEW" ? "Continue →" : "Start Review →"}
                </Button>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}